import { unwrap } from "@sagot/api-client";
import { BACKUP_CODE_PATTERN, CONSOLE_PATHS, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Avatar, Button, Icon, Input, toast } from "@sagot/ui";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { Navigate } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { clearPendingAuth, readPendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";
import { OtpInput, isCompleteOtp } from "./components/otp-input";
import { useCompleteSignIn } from "./use-sign-in";

type Mode = "app" | "backup";

function Identity({ email }: { email: string }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 flex items-center gap-2.5 rounded-md border border-line bg-surface px-3 py-2">
      <Avatar name={email} className="size-7" />
      <span className="min-w-0 flex-1 truncate text-xs">
        <span className="font-bold text-fg">{email}</span>
      </span>
      <AppLink href={CONSOLE_PATHS.login} className="text-xs font-semibold text-primary-ink hover:underline" onClick={clearPendingAuth}>
        {t("auth.twoFactor.notYou")}
      </AppLink>
    </div>
  );
}

function RecoveryHelp({ challengeId }: { challengeId: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const request = async () => {
    try {
      await unwrap(api.POST("/v1/auth/2fa/recovery-request", { body: { challenge_id: challengeId } }));
      toast.info(t("auth.twoFactor.recoverySent"));
    } catch (caught) {
      toast.error(message(caught));
    }
  };
  return (
    <div className="mt-8 rounded-lg border border-line bg-surface px-4 py-3 text-xs text-fg-muted">
      <div className="flex items-start gap-2">
        <Icon name="life-buoy" className="mt-0.5 size-3.5 text-fg-subtle" />
        <span>
          {t("auth.twoFactor.helpLead")}{" "}
          <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={() => void request()}>
            {t("auth.twoFactor.helpLink")}
          </button>
          .
        </span>
      </div>
    </div>
  );
}

export function TwoFactorPage() {
  const { t } = useTranslation();
  const api = useApi();
  const pending = readPendingAuth();
  const completeSignIn = useCompleteSignIn();
  const message = useErrorMessage();
  const [mode, setMode] = useState<Mode>("app");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const backupRef = useRef<HTMLInputElement>(null);
  const switched = useRef(false);

  useEffect(() => {
    if (!switched.current) return;
    if (mode === "backup") backupRef.current?.focus();
    else document.getElementById("tf-1")?.focus();
  }, [mode]);

  if (pending?.kind !== "two-factor") return <Navigate to={CONSOLE_PATHS.login} replace />;
  const ready = mode === "app" ? isCompleteOtp(code) : BACKUP_CODE_PATTERN.test(backup.trim().toUpperCase());

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      const credential = mode === "app" ? { code } : { backup_code: backup.trim().toUpperCase() };
      const body = await unwrap(api.POST("/v1/auth/2fa/verify", { body: { challenge_id: pending.challengeId, trust_device: trust, ...credential } }));
      await completeSignIn(body, { email: pending.email, next: pending.next });
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMode = () => {
    switched.current = true;
    setError(null);
    setMode((m) => (m === "app" ? "backup" : "app"));
  };

  return (
    <AuthLayout title={t("auth.twoFactor.title", { product: PRODUCT_NAME })} back={{ label: t("auth.twoFactor.differentAccount"), href: CONSOLE_PATHS.login }}>
      <IconBadge icon="shield-check" />
      <h1 className="mt-5 text-2xl font-extrabold tracking-tight">{t("auth.twoFactor.heading")}</h1>
      <p className="mt-1 text-fg-muted" aria-live="polite">
        {mode === "app" ? (
          <>
            {t("auth.twoFactor.appSubLead")} <strong className="text-fg">{`${PRODUCT_NAME} · ${pending.email.split("@")[0] ?? pending.email}`}</strong>.
          </>
        ) : (
          t("auth.twoFactor.backupSub")
        )}
      </p>
      <Identity email={pending.email} />
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mt-5" /> : null}
      <form className="mt-6" noValidate onSubmit={(event) => void onSubmit(event)}>
        {mode === "app" ? (
          <div>
            <label className="label" htmlFor="tf-1">
              {t("auth.twoFactor.codeLabel")}
            </label>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the code is the page's only task (mockup autofocus) */}
            <OtpInput id="tf-1" value={code} onChange={setCode} autoFocus invalid={Boolean(error)} describedBy="tf-hint" />
            <p className="hint" id="tf-hint">
              {t("auth.twoFactor.codeHint")}
            </p>
          </div>
        ) : (
          <div>
            <label className="label" htmlFor="tf-backup-input">
              {t("auth.twoFactor.backupLabel")}
            </label>
            <Input
              ref={backupRef}
              id="tf-backup-input"
              className="font-mono uppercase tracking-[0.2em]"
              placeholder="XXXX-XXXX"
              maxLength={9}
              autoComplete="off"
              aria-describedby="tf-backup-hint"
              value={backup}
              onChange={(event) => {
                setBackup(event.target.value);
              }}
            />
            <p className="hint" id="tf-backup-hint">
              {t("auth.twoFactor.backupHint")}
            </p>
          </div>
        )}
        <label className="mt-5 flex items-center gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            className="checkbox"
            checked={trust}
            onChange={(event) => {
              setTrust(event.target.checked);
            }}
          />
          {t("auth.twoFactor.trust")}
        </label>
        <Button type="submit" size="lg" className="mt-5 w-full" loading={submitting} disabled={submitting || !ready}>
          {t("auth.twoFactor.submit")}
        </Button>
      </form>
      <div className="mt-5 text-center">
        <button type="button" className="text-sm font-semibold text-primary-ink hover:underline" onClick={toggleMode}>
          {mode === "app" ? t("auth.twoFactor.useBackup") : t("auth.twoFactor.useApp")}
        </button>
      </div>
      <RecoveryHelp challengeId={pending.challengeId} />
    </AuthLayout>
  );
}
